import type { User } from '@/types';
import { hasScopedAccessLevel } from '@/types';
import { getActiveScopedRelationships } from '@/lib/auth/scopedRelationshipResolver';

let actor: User | null = null;

/** Keeps demo mutations tied to the same active identity shown in the UI. */
export function setDemoAuthorityActor(user: User | null): void {
  actor = user;
}

/** Returns the identity currently driving demo authorization checks. */
export function getDemoAuthorityActor(): User | null {
  return actor;
}

/** Boolean form for compatibility checks that should fail closed. */
export function canDemoManageProject(projectId: string): boolean {
  if (!actor) return false;

  const canManageByOrganization = actor.account_role === 'organization_owner'
    || actor.account_role === 'organization_admin';
  const canManageByProjectScope = getActiveScopedRelationships(actor).some((relationship) =>
    relationship.type === 'project_manager'
    && relationship.resourceType === 'project'
    && relationship.resourceId === projectId
    && hasScopedAccessLevel(relationship.accessLevel, 'manage'),
  );

  return canManageByOrganization || canManageByProjectScope;
}

/** Platform configuration is control-plane authority, never customer admin authority. */
export function canDemoManagePlatformConfiguration(): boolean {
  return actor?.account_role === 'platform_owner' || actor?.account_role === 'platform_admin';
}

/** Demo mirror of the project-management boundary used by the secure RPC. */
export function assertDemoCanManageProject(projectId: string): void {
  if (!actor) throw new Error('Sign in with a project manager persona to manage participants.');
  if (!canDemoManageProject(projectId)) {
    throw new Error('Project participation management is not allowed for this demo persona.');
  }
}

/** Demo mirror of the organization administration boundary used by secure RPCs. */
export function assertDemoCanAdministerOrganization(): void {
  if (!actor) throw new Error('Sign in with a Workspace Admin persona to manage Workspace Members.');
  if (actor.account_role !== 'organization_owner' && actor.account_role !== 'organization_admin') {
    throw new Error('Workspace administration is not allowed for this demo persona.');
  }
}
